import React from 'react';
import { useSelector } from 'react-redux';

import RouteEnum, { ExternalLinks } from '../../RouteEnum';
import { Country } from '../../services/Country';
import { Language } from '../../services/Language';
import OneTrust from '../../services/OneTrust';
import { Platform } from '../../services/Platform';
import Tracking from '../../services/Tracking';
import type { ReduxState } from '../../store';
import Button from '../Button';
import { HoppyGoLogo } from '../Icons/HoppyGoLogo';
import Link from '../Link';
import { androidMobileAppLink, iosMobileAppLink } from '../MobileAppLinks';
import { Restriction, useRegion } from '../Restriction';
import T from '../Translate';

import styles from './footer.module.scss';

export function Footer() {
  const { lang } = useSelector((store: ReduxState) => ({
    lang: store.app.language,
  }));

  const handleCookiesClick = () => {
    OneTrust.openCookieSettings();
  };

  return (
    <footer className={styles.container}>
      <div className={styles.section}>
        <HoppyGoLogo className={styles.logo} />
      </div>
      <div className={styles.section}>
        <div className={styles.subsection}>
          <T as="h5" id="footer.header.company" />
          <div className={styles.links}>
            <Link onClick={() => Tracking.track('FOOTER_ABOUT_US_CLICKED')} to={RouteEnum.STATIC_ABOUT_US}>
              <T id="footer.aboutUs" />
            </Link>
            <Link onClick={() => Tracking.track('FOOTER_FAQ_CLICKED')} to={RouteEnum.STATIC_FAQ}>
              <T id="footer.faq" />
            </Link>
            <Link onClick={() => Tracking.track('FOOTER_PARTNERS_CLICKED')} to={RouteEnum.STATIC_PARTNERS}>
              <T id="footer.partners" />
            </Link>
            <Restriction language={Language.CS} only>
              <Link href={ExternalLinks.ZENY_AUTA_PUJCUJI} rel="noreferrer noopener" target="_blank">
                <T id="footer.external.zenyautapujcuji" />
              </Link>
            </Restriction>
            <Link onClick={() => Tracking.track('FOOTER_CONTACT_CLICKED')} to={RouteEnum.STATIC_CONTACT}>
              <T id="footer.contact" />
            </Link>
          </div>
        </div>
        <div className={styles.subsection}>
          <T as="h5" id="footer.header.documents" />
          <div className={styles.links}>
            <Link onClick={() => Tracking.track('FOOTER_PRIVACY_CLICKED')} to={RouteEnum.STATIC_PRIVACY_POLICY}>
              <T id="footer.privacyPolicy" />
            </Link>
            <Link onClick={() => Tracking.track('FOOTER_TERMS_CLICKED')} to={RouteEnum.STATIC_TERMS}>
              <T id="footer.termsAndConditions" />
            </Link>
            <Link
              onClick={() => Tracking.track('FOOTER_GOVERNANCE_CLICKED')}
              to={RouteEnum.STATIC_CORPORATE_GOVERNANCE}
            >
              <T id="footer.corporateGovernance" />
            </Link>
            <Link
              hash="code-of-conduct"
              onClick={() =>
                Tracking.track('FOOTER_GOVERNANCE_CLICKED', {
                  part: 'code-of-conduct',
                })
              }
              to={RouteEnum.STATIC_CORPORATE_GOVERNANCE}
            >
              <T id="footer.corporateGovernance.codeOfConduct" />
            </Link>
            <Link
              hash="whistleblower"
              onClick={() =>
                Tracking.track('FOOTER_GOVERNANCE_CLICKED', {
                  part: 'whistleblower',
                })
              }
              to={RouteEnum.STATIC_CORPORATE_GOVERNANCE}
            >
              <T id="footer.corporateGovernance.whistleblower" />
            </Link>
            <Button onClick={handleCookiesClick}>
              <T id="footer.cookies" />
            </Button>
          </div>
        </div>
        <div className={styles.subsection}>
          <div className={styles.subsection}>
            <T as="h5" id="footer.header.socials" />
            <SocialLinks />
          </div>
          <div className={styles.subsection}>
            <T as="h5" id="footer.header.mobileApps" />
            <div className={styles.linksRow}>
              <a
                href={iosMobileAppLink(lang)}
                onClick={() => Tracking.track('FOOTER_MOBILE_APP_LINK_CLICKED', Platform.IOS)}
                rel="noreferrer noopener"
                target="_blank"
              >
                <i className="icon icon-apple" />
              </a>
              <a
                href={androidMobileAppLink()}
                onClick={() => Tracking.track('FOOTER_MOBILE_APP_LINK_CLICKED', Platform.ANDROID)}
                rel="noreferrer noopener"
                target="_blank"
              >
                <i className="icon icon-android" />
              </a>
            </div>
          </div>
        </div>
      </div>
      <div className={styles.copyright}>
        <T as="p" className="mb-0" id="footer.copyright" />
      </div>
    </footer>
  );
}

const socialLinks = {
  [Country.CZ]: {
    blog: 'https://blog.hoppygo.com/',
    instagram: 'https://www.instagram.com/hoppygo.czsk/',
    facebook: 'https://www.facebook.com/HoppyGoCZ',
    youtube: 'https://www.youtube.com/channel/UC02im0QCQmXutpteq9A6qDQ',
    linkedin: 'https://www.linkedin.com/company/hoppygo/',
  },
  [Country.SK]: {
    blog: 'https://blog.hoppygo.com/sk',
    instagram: 'https://www.instagram.com/hoppygo.czsk/',
    facebook: 'https://www.facebook.com/HoppyGoSK',
    youtube: 'https://www.youtube.com/channel/UC02im0QCQmXutpteq9A6qDQ',
    linkedin: 'https://www.linkedin.com/company/hoppygo/',
  },
};

function SocialLinks() {
  const [region] = useRegion();
  const links = socialLinks[region];

  const handleClick = (type: string) => () => {
    Tracking.track('FOOTER_SOCIAL_LINK_CLICKED', type);
  };

  return (
    <div className={styles.linksRow}>
      <a href={links.blog} rel="noopener noreferrer" target="_blank">
        Blog
      </a>
      <a href={links.instagram} onClick={handleClick('instagram')} rel="noopener noreferrer" target="_blank">
        <i className="icon icon-instagram" />
      </a>
      <a href={links.facebook} onClick={handleClick('facebook')} rel="noopener noreferrer" target="_blank">
        <i className="icon icon-facebook-f" />
      </a>
      <a href={links.youtube} onClick={handleClick('youtube')} rel="noopener noreferrer" target="_blank">
        <i className="icon icon-youtube" />
      </a>
      <a href={links.linkedin} onClick={handleClick('linkedin')} rel="noopener noreferrer" target="_blank">
        <i className="icon icon-linkedin" />
      </a>
    </div>
  );
}

export default Footer;
